import { Spacing } from '@persona-id/design-tokens';
import { FC, PropsWithChildren, ReactNode, useCallback } from 'react';

import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import Button, { ButtonProps } from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

interface ConfirmationModalProps {
  cancelAction: () => void;
  confirmAction: (() => void) | (() => Promise<void>);
  header?: ReactNode;
  cancelActionText?: string;
  confirmActionText: string;
  confirmButtonType?: ButtonProps['variant'];
  size?: 'md' | 'lg' | 'xl';
  allowConfirm?: boolean;
}

const ConfirmationModal: FC<PropsWithChildren<ConfirmationModalProps>> = ({
  cancelAction,
  confirmAction,
  header = 'Are you sure?',
  cancelActionText = 'Cancel',
  confirmActionText,
  confirmButtonType = 'danger',
  size = 'md',
  children = 'This action is permanent and can’t be undone.',
  allowConfirm = true,
}) => {
  const [isPending, { enable, disable }] = useToggle(false);

  const handleConfirm = useCallback(async () => {
    enable();
    // If confirmAction doesn't return a promise, this will be synchronous
    const maybePromise = confirmAction();

    if (maybePromise instanceof Promise) {
      await maybePromise;
    }
    disable();
    // close modal on complete
    cancelAction();
  }, [cancelAction, confirmAction, disable, enable]);

  return (
    <ModalContainer data-test='confirmation-modal' size={size}>
      <ModalHeader
        data-test='confirmation-modal__header'
        onClose={isPending ? undefined : cancelAction}
      >
        {header}
      </ModalHeader>

      <ModalBody>
        <Paragraph data-test='confirmation-modal__message'>{children}</Paragraph>

        <Spacer height={4} />

        <HStack equal={true} spacing={Spacing[4]}>
          <Button
            autoFocus={true}
            id='confirmation-modal__cancel'
            data-test='confirmation-modal__cancel'
            variant='basic'
            onClick={cancelAction}
            disabled={isPending}
          >
            {cancelActionText}
          </Button>
          <Button
            id='confirmation-modal__confirm'
            data-test='confirmation-modal__confirm'
            variant={confirmButtonType}
            onClick={handleConfirm}
            isPending={isPending}
            disabled={isPending || !allowConfirm}
          >
            {confirmActionText}
          </Button>
        </HStack>
      </ModalBody>
    </ModalContainer>
  );
};

export default ConfirmationModal;
